import type {
  Archivos, Configuracion, DocumentoDetalle, DocumentoResumen, EmisorInput, ErrorCarga, FacturacionApi, Pagina, Productos,
  RucConsulta, Ubicacion,
} from './types';

/** Error devuelto por la API: { titulo, detalles[], codigoAlanube? } */
export class ApiError extends Error {
  constructor(
    public status: number,
    public titulo: string,
    public detalles: string[] = [],
    public codigoAlanube: string | null = null,
  ) {
    super(titulo);
  }
}

export interface OpcionesApi {
  /** URL base del API (sin /api/pa/fe). En el portal: el servidor de Swiftmart.API, que reenvía a SwiftWebApi. */
  baseUrl: string;
  /** Devuelve el JWT de la sesión del portal. */
  obtenerToken: () => string | null | Promise<string | null>;
}

export function crearApiHttp({ baseUrl, obtenerToken }: OpcionesApi): FacturacionApi {
  const base = baseUrl.replace(/\/$/, '') + '/api/pa/fe';

  async function req<T>(metodo: string, ruta: string, cuerpo?: FormData | object): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    const token = await obtenerToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    let body: BodyInit | undefined;
    if (cuerpo instanceof FormData) body = cuerpo;
    else if (cuerpo !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(cuerpo);
    }

    let resp: Response;
    try {
      resp = await fetch(base + ruta, { method: metodo, headers, body });
    } catch {
      throw new ApiError(0, 'No se pudo conectar con el servidor.');
    }

    const texto = await resp.text();
    const json = texto ? safeParse(texto) : null;
    if (!resp.ok) {
      if (resp.status === 401) throw new ApiError(401, 'Su sesión expiró. Vuelva a ingresar.');
      if (resp.status === 403 && !json) throw new ApiError(403, 'No tiene permiso para configurar este comercio.');
      throw new ApiError(
        resp.status,
        json?.titulo ?? `Error ${resp.status}`,
        Array.isArray(json?.detalles) ? json.detalles : [],
        json?.codigoAlanube ?? null,
      );
    }
    return json as T;
  }

  const c = (id: number) => `/comercios/${id}`;

  return {
    obtenerConfiguracion: (id) => req<Configuracion>('GET', `${c(id)}/configuracion`),
    ubicaciones: () => req<Ubicacion[]>('GET', '/catalogos/ubicaciones'),
    consultarRuc: (id, ruc, tipo) =>
      req<RucConsulta>('GET', `${c(id)}/ruc?ruc=${encodeURIComponent(ruc)}&tipo=${tipo}`),
    guardarEmisor: (id, d) => req<Configuracion>('PUT', `${c(id)}/emisor`, aFormData(d)),
    crearSucursal: (id, d) => req<Configuracion>('POST', `${c(id)}/sucursales`, d),
    actualizarSucursal: (id, sid, d) => req<Configuracion>('PUT', `${c(id)}/sucursales/${sid}`, d),
    afiliar: (id) => req<Configuracion>('POST', `${c(id)}/afiliacion`),
    generarQr: (id) => req<Configuracion>('POST', `${c(id)}/qr/generar`),
    registrarQr: (id, qr) => req<Configuracion>('PUT', `${c(id)}/qr`, { qr }),
    sincronizar: (id) => req<Configuracion>('POST', `${c(id)}/sincronizar`),
    productos: (id, estado, buscar) => req<Productos>('GET', `${c(id)}/productos${qs({ estado, buscar })}`),
    documentos: (id, f) => req<Pagina<DocumentoResumen>>('GET', `${c(id)}/documentos${qs({ ...f, pagina: f.pagina ?? 1 })}`),
    documento: (id, doc) => req<DocumentoDetalle>('GET', `${c(id)}/documentos/${doc}`),
    reintentar: (id, doc) => req<DocumentoDetalle>('POST', `${c(id)}/documentos/${doc}/reintentar`),
    reenviarCorreo: (id, doc, email) => req<DocumentoDetalle>('POST', `${c(id)}/documentos/${doc}/reenviar-correo`, { email }),
    archivos: (id, doc) => req<Archivos>('GET', `${c(id)}/documentos/${doc}/archivos`),
    erroresCarga: (id) => req<ErrorCarga[]>('GET', `${c(id)}/errores-carga`),
    recargarFactura: (id, n) => req<ErrorCarga[]>('POST', `${c(id)}/errores-carga/${n}/recargar`),
  };
}

function qs(params: Record<string, string | number | undefined | null>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const t = p.toString();
  return t ? `?${t}` : '';
}

function aFormData(d: EmisorInput): FormData {
  const f = new FormData();
  f.append('TipoRuc', String(d.tipoRuc));
  f.append('Ruc', d.ruc.trim());
  if (d.dv) f.append('Dv', d.dv);
  if (d.razonSocial) f.append('RazonSocial', d.razonSocial);
  if (d.nombreComercial.trim()) f.append('NombreComercial', d.nombreComercial.trim());
  // El correo al cliente lo envía el sistema: el envío de correos del PAC queda siempre apagado.
  f.append('EmailActivo', 'false');
  if (d.certificadoFirma) {
    f.append('CertificadoFirma', d.certificadoFirma);
    f.append('CertificadoFirmaPassword', d.certificadoFirmaPassword);
  }
  if (d.certificadoAutenticacion) {
    f.append('CertificadoAutenticacion', d.certificadoAutenticacion);
    f.append('CertificadoAutenticacionPassword', d.certificadoAutenticacionPassword);
  }
  if (d.logo) f.append('Logo', d.logo);
  return f;
}

function safeParse(t: string): any {
  try {
    return JSON.parse(t);
  } catch {
    return null;
  }
}
